import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useAxios } from './use-axios';

const USER_NOTIFICATION_SETTINGS_QUERY_KEY = ['settings', 'notifications', 'user'];
const SYSTEM_NOTIFICATION_SETTINGS_QUERY_KEY = ['settings', 'notifications', 'system'];

// ============================================================================
// USER NOTIFICATION TYPES (per-user preferences)
// ============================================================================

export type UserNotificationCategory = 'manual_task_assignment';

export type UserNotificationPreferences = Record<UserNotificationCategory, boolean>;

// User notification category metadata for UI rendering
export const USER_NOTIFICATION_CATEGORY_INFO: Record<
  UserNotificationCategory,
  { label: string; description: string }
> = {
  manual_task_assignment: {
    label: 'Task Assignment Emails',
    description: 'Receive email notifications when you are assigned to a task',
  },
};

// ============================================================================
// SYSTEM NOTIFICATION TYPES (global settings)
// These control automated emails to role-based users configured via environment variables
// ============================================================================

export type SystemNotificationCategory
  = | 'new_business_tasks'
    | 'contact_modification_tasks'
    | 'verification_tasks'
    | 'public_verification_tasks';

export type SystemNotificationPreferences = Record<SystemNotificationCategory, boolean>;

// System notification category metadata for UI rendering
export const SYSTEM_NOTIFICATION_CATEGORY_INFO: Record<
  SystemNotificationCategory,
  { label: string; description: string; recipient: string }
> = {
  new_business_tasks: {
    label: 'New Business Onboarding Emails',
    description: 'System emails sent when new business applications are submitted through the public onboarding form',
    recipient: 'Configured via admin',
  },
  contact_modification_tasks: {
    label: 'Contact Modification Emails',
    description: 'System emails sent when the listener detects contact changes that require review',
    recipient: 'VERIFY_USER_ID',
  },
  verification_tasks: {
    label: 'Verification Deal Emails',
    description: 'System emails sent when annual or forced verification deals are created',
    recipient: 'VERIFY_USER_ID',
  },
  public_verification_tasks: {
    label: 'Public Verification Emails',
    description: 'System emails sent when contacts submit verification through public links',
    recipient: 'VERIFY_USER_ID',
  },
};

// Order in which system categories should be displayed
export const SYSTEM_NOTIFICATION_CATEGORY_ORDER: SystemNotificationCategory[] = [
  'new_business_tasks',
  'verification_tasks',
  'contact_modification_tasks',
  'public_verification_tasks',
];

// ============================================================================
// API RESPONSE TYPES
// ============================================================================

interface ApiSuccessResponse<T> {
  status: 'success';
  data: T;
}

interface ApiError {
  error?: {
    message?: string;
    status?: number;
  };
  details?: Array<{ message: string }>;
}

function getErrorMessage(error: unknown): string {
  if (isAxiosError<ApiError>(error)) {
    if (error.response?.data?.details) {
      return 'Please check your input and try again';
    }
    if (error.response?.status === 500) {
      return 'An internal server error occurred. Please try again later';
    }
    if (error.response?.status === 401 || error.response?.status === 403) {
      return 'You do not have permission to perform this action';
    }
    if (error.message === 'Network Error') {
      return 'Unable to connect to the server. Please check your internet connection';
    }
  }
  return 'An unexpected error occurred. Please try again';
}

// ============================================================================
// USER NOTIFICATION HOOKS
// ============================================================================

/**
 * Hook to get all user notification preferences for the current user
 */
export function useUserNotificationPreferences() {
  const axios = useAxios();
  return useQuery({
    queryKey: USER_NOTIFICATION_SETTINGS_QUERY_KEY,
    queryFn: async () => {
      try {
        const { data } = await axios.get<ApiSuccessResponse<UserNotificationPreferences>>(
          '/settings/notifications',
        );
        return data.data;
      } catch (error) {
        throw new Error(getErrorMessage(error));
      }
    },
    gcTime: Infinity,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook to toggle a single user notification category
 */
export function useToggleUserNotificationCategory() {
  const axios = useAxios();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      category,
      enabled,
    }: {
      category: UserNotificationCategory;
      enabled: boolean;
    }) => {
      try {
        const { data } = await axios.put<ApiSuccessResponse<UserNotificationPreferences>>(
          '/settings/notifications',
          { [category]: enabled },
        );
        return data.data;
      } catch (error) {
        throw new Error(getErrorMessage(error));
      }
    },
    onMutate: async ({ category, enabled }) => {
      await queryClient.cancelQueries({ queryKey: USER_NOTIFICATION_SETTINGS_QUERY_KEY });

      const previousPreferences = queryClient.getQueryData<UserNotificationPreferences>(
        USER_NOTIFICATION_SETTINGS_QUERY_KEY,
      );

      if (previousPreferences) {
        queryClient.setQueryData<UserNotificationPreferences>(USER_NOTIFICATION_SETTINGS_QUERY_KEY, {
          ...previousPreferences,
          [category]: enabled,
        });
      }

      return { previousPreferences };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousPreferences) {
        queryClient.setQueryData(USER_NOTIFICATION_SETTINGS_QUERY_KEY, context.previousPreferences);
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: USER_NOTIFICATION_SETTINGS_QUERY_KEY });
    },
  });
}

// ============================================================================
// SYSTEM NOTIFICATION HOOKS
// ============================================================================

/**
 * Hook to get all system notification preferences (global settings)
 */
export function useSystemNotificationPreferences() {
  const axios = useAxios();
  return useQuery({
    queryKey: SYSTEM_NOTIFICATION_SETTINGS_QUERY_KEY,
    queryFn: async () => {
      try {
        const { data } = await axios.get<ApiSuccessResponse<SystemNotificationPreferences>>(
          '/settings/notifications/system',
        );
        return data.data;
      } catch (error) {
        throw new Error(getErrorMessage(error));
      }
    },
    gcTime: Infinity,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook to toggle a single system notification category
 */
export function useToggleSystemNotificationCategory() {
  const axios = useAxios();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      category,
      enabled,
    }: {
      category: SystemNotificationCategory;
      enabled: boolean;
    }) => {
      try {
        const { data } = await axios.put<ApiSuccessResponse<SystemNotificationPreferences>>(
          '/settings/notifications/system',
          { [category]: enabled },
        );
        return data.data;
      } catch (error) {
        throw new Error(getErrorMessage(error));
      }
    },
    onMutate: async ({ category, enabled }) => {
      await queryClient.cancelQueries({ queryKey: SYSTEM_NOTIFICATION_SETTINGS_QUERY_KEY });

      const previousPreferences = queryClient.getQueryData<SystemNotificationPreferences>(
        SYSTEM_NOTIFICATION_SETTINGS_QUERY_KEY,
      );

      if (previousPreferences) {
        queryClient.setQueryData<SystemNotificationPreferences>(
          SYSTEM_NOTIFICATION_SETTINGS_QUERY_KEY,
          {
            ...previousPreferences,
            [category]: enabled,
          },
        );
      }

      return { previousPreferences };
    },
    onError: (_error, _variables, context) => {
      if (context?.previousPreferences) {
        queryClient.setQueryData(
          SYSTEM_NOTIFICATION_SETTINGS_QUERY_KEY,
          context.previousPreferences,
        );
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: SYSTEM_NOTIFICATION_SETTINGS_QUERY_KEY });
    },
  });
}

// ============================================================================
// NEW BUSINESS USER CONFIG HOOKS
// ============================================================================

const NEW_BUSINESS_USER_CONFIG_QUERY_KEY = ['settings', 'config', 'new-business-user'];

export interface NewBusinessUserConfig {
  userId: string;
  source: 'config' | 'environment';
}

/**
 * Hook to get the configured new business user ID and its source
 */
export function useNewBusinessUserConfig() {
  const axios = useAxios();
  return useQuery({
    queryKey: NEW_BUSINESS_USER_CONFIG_QUERY_KEY,
    queryFn: async () => {
      try {
        const { data } = await axios.get<ApiSuccessResponse<NewBusinessUserConfig>>(
          '/settings/config/new-business-user',
        );
        return data.data;
      } catch (error) {
        throw new Error(getErrorMessage(error));
      }
    },
    gcTime: Infinity,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook to update the new business user ID
 */
export function useSetNewBusinessUser() {
  const axios = useAxios();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      try {
        const { data } = await axios.put<ApiSuccessResponse<NewBusinessUserConfig>>(
          '/settings/config/new-business-user',
          { userId },
        );
        return data.data;
      } catch (error) {
        throw new Error(getErrorMessage(error));
      }
    },
    onMutate: async (userId) => {
      await queryClient.cancelQueries({ queryKey: NEW_BUSINESS_USER_CONFIG_QUERY_KEY });
      const previous = queryClient.getQueryData<NewBusinessUserConfig>(NEW_BUSINESS_USER_CONFIG_QUERY_KEY);
      queryClient.setQueryData<NewBusinessUserConfig>(NEW_BUSINESS_USER_CONFIG_QUERY_KEY, {
        userId,
        source: 'config',
      });
      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(NEW_BUSINESS_USER_CONFIG_QUERY_KEY, context.previous);
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: NEW_BUSINESS_USER_CONFIG_QUERY_KEY });
    },
  });
}

// ============================================================================
// NEW BUSINESS ADDITIONAL RECIPIENTS HOOKS
// ============================================================================

const NEW_BUSINESS_ADDITIONAL_RECIPIENTS_QUERY_KEY = ['settings', 'config', 'new-business-additional-recipients'];

export interface NewBusinessAdditionalRecipientsConfig {
  userIds: string[];
}

/**
 * Hook to get the additional users who receive new business task emails
 */
export function useNewBusinessAdditionalRecipients() {
  const axios = useAxios();
  return useQuery({
    queryKey: NEW_BUSINESS_ADDITIONAL_RECIPIENTS_QUERY_KEY,
    queryFn: async () => {
      try {
        const { data } = await axios.get<ApiSuccessResponse<NewBusinessAdditionalRecipientsConfig>>(
          '/settings/config/new-business-additional-recipients',
        );
        return data.data;
      } catch (error) {
        throw new Error(getErrorMessage(error));
      }
    },
    gcTime: Infinity,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  });
}

/**
 * Hook to update the additional recipients list
 */
export function useSetNewBusinessAdditionalRecipients() {
  const axios = useAxios();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userIds: string[]) => {
      try {
        const { data } = await axios.put<ApiSuccessResponse<NewBusinessAdditionalRecipientsConfig>>(
          '/settings/config/new-business-additional-recipients',
          { userIds },
        );
        return data.data;
      } catch (error) {
        throw new Error(getErrorMessage(error));
      }
    },
    onMutate: async (userIds) => {
      await queryClient.cancelQueries({ queryKey: NEW_BUSINESS_ADDITIONAL_RECIPIENTS_QUERY_KEY });
      const previous = queryClient.getQueryData<NewBusinessAdditionalRecipientsConfig>(
        NEW_BUSINESS_ADDITIONAL_RECIPIENTS_QUERY_KEY,
      );
      queryClient.setQueryData<NewBusinessAdditionalRecipientsConfig>(
        NEW_BUSINESS_ADDITIONAL_RECIPIENTS_QUERY_KEY,
        { userIds },
      );
      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(NEW_BUSINESS_ADDITIONAL_RECIPIENTS_QUERY_KEY, context.previous);
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: NEW_BUSINESS_ADDITIONAL_RECIPIENTS_QUERY_KEY });
    },
  });
}

// ============================================================================
// LEGACY EXPORTS (for backwards compatibility)
// ============================================================================

/** @deprecated Use useUserNotificationPreferences instead */
export const useNotificationPreferences = useUserNotificationPreferences;

/** @deprecated Use useToggleUserNotificationCategory instead */
export const useToggleNotificationCategory = useToggleUserNotificationCategory;

/** @deprecated Use UserNotificationCategory instead */
export type NotificationCategory = UserNotificationCategory | SystemNotificationCategory;

/** @deprecated Use USER_NOTIFICATION_CATEGORY_INFO instead */
export const NOTIFICATION_CATEGORY_INFO = USER_NOTIFICATION_CATEGORY_INFO;

/** @deprecated Use UserNotificationPreferences instead */
export type NotificationPreferences = UserNotificationPreferences;

/** @deprecated No longer used */
export const NOTIFICATION_CATEGORY_ORDER: UserNotificationCategory[] = ['manual_task_assignment'];
